import { get } from 'lodash';
import {
  type AdditionalFormError,
  type NamePath,
  ValidationErrorType,
} from '@xbcb/ui-types';
import type { Errors } from 'types';

interface ValidationOptions {
  path?: NamePath;
  excludedKeys?: string[];
  showMissingFields?: boolean;
}

export class ValidationErrors {
  private readonly errors: Errors;
  constructor(errors: Errors) {
    this.errors = errors;
  }

  getErrorsByNamePath(path: NamePath) {
    return get(this.errors, [...path, 'errors']);
  }

  getErrorMessages(options: ValidationOptions = {}) {
    const tree = options.path ? get(this.errors, options.path) : this.errors;
    return (
      this.search(tree, options)
        // We don't display individual "Field X is required" messages in error alerts
        .filter((error) =>
          options.showMissingFields
            ? true
            : error.type !== ValidationErrorType.MISSING_FIELD,
        )
        .flatMap((error) => error.messages)
    );
  }

  getErrorsByType(
    errorTypes: ValidationErrorType[],
    options: ValidationOptions = {},
  ) {
    const tree = options.path ? get(this.errors, options.path) : this.errors;
    const errors = this.search(tree, { ...options });
    const result = errors.filter((error) =>
      errorTypes.includes(error.type as ValidationErrorType),
    );
    return result;
  }

  hasErrors(options: ValidationOptions = {}) {
    return (
      this.getErrorsByType(
        [ValidationErrorType.GENERAL, ValidationErrorType.SNAPSHOT],
        options,
      ).length > 0
    );
  }

  hasMissingFields(options: ValidationOptions = {}): boolean {
    return (
      this.getErrorsByType([ValidationErrorType.MISSING_FIELD], options)
        .length > 0
    );
  }

  hasPath(path: NamePath): boolean {
    return get(this.errors, path) !== undefined;
  }

  private search(
    errors: Errors,
    options: ValidationOptions = {},
  ): AdditionalFormError[] {
    const { excludedKeys = [] } = options;
    const results: AdditionalFormError[] = [];

    if (errors?.errors) {
      const messages = errors.errors;
      results.push(...messages);
    }
    for (const key in errors) {
      if (
        key !== 'errors' &&
        !excludedKeys.includes(key) &&
        typeof errors[key] === 'object'
      ) {
        const childErrors = this.search(errors[key] as Errors, {
          excludedKeys,
        });
        results.push(...childErrors);
      }
    }

    return results;
  }
}
